import React, { useMemo } from "react";
import { FaTimes } from "react-icons/fa";
import {
  applyProviderOnlyPreset,
  areInvoiceDisplayOptionsDefault,
  defaultInvoiceDisplayOptions,
  normalizeInvoiceDisplayOptions,
} from "../reports/commissions/commissionInvoiceDisplayOptions";

function CheckRow({
  id,
  label,
  checked,
  disabled,
  onChange,
}) {
  return (
    <label
      htmlFor={id}
      className={`group flex cursor-pointer select-none items-center gap-1.5 rounded px-1 py-[2px] text-[11px] transition-colors ${
        disabled ? "cursor-not-allowed opacity-45" : "hover:bg-slate-100/80"
      }`}
    >
      <input
        id={id}
        type="checkbox"
        className="h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-1 focus:ring-indigo-500/40 disabled:opacity-50"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="min-w-0 flex-1 leading-tight text-slate-700">{label}</span>
    </label>
  );
}

function SectionBlock({ title, children, disabled: sectionDisabled }) {
  return (
    <div
      className={`border-b border-slate-100 py-1.5 last:border-b-0 ${
        sectionDisabled ? "opacity-50" : ""
      }`}
    >
      <div className="mb-1 px-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
        {title}
      </div>
      {children}
    </div>
  );
}

function NestedGrid({ children }) {
  return (
    <div className="ml-4 grid grid-cols-2 gap-x-1 gap-y-0 border-l border-slate-100 pl-2">
      {children}
    </div>
  );
}

function InvoicePreviewCustomizePanel({
  displayOptions,
  onChange,
  onClose,
  readOnly = false,
  saving = false,
}) {
  const opts = normalizeInvoiceDisplayOptions(displayOptions);
  const isDirty = useMemo(
    () => !areInvoiceDisplayOptionsDefault(opts),
    [opts],
  );

  const patchGroup = (group, key, value) => {
    onChange?.({
      ...opts,
      [group]: {
        ...opts[group],
        [key]: value,
      },
    });
  };

  const disabled = readOnly || saving;
  const toOff = opts.to.showSection === false;
  const fromOff = opts.from.showSection === false;
  const detailsOff = opts.details.showSection === false;
  const adviceOff = opts.footer.showPaymentAdvice === false;

  return (
    <div className="invoice-preview-customize-panel tailwind-container flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="invoice-preview-customize-panel__head shrink-0 border-b border-slate-100 px-2.5 py-2">
        {typeof onClose === "function" ? (
          <button
            type="button"
            className="invoice-preview-customize-panel__close"
            aria-label="Close preview"
            onClick={onClose}
          >
            <FaTimes size={11} />
          </button>
        ) : null}
        <div className="invoice-preview-customize-panel__head-inner">
          <div className="flex items-center gap-1.5">
            <p className="invoice-preview-customize-panel__title min-w-0 flex-1">
              Invoice customization
            </p>
            {isDirty && !saving ? (
              <button
                type="button"
                disabled={disabled}
                className="invoice-preview-customize-panel__reset shrink-0 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[9px] font-semibold text-slate-600 shadow-sm hover:bg-slate-50 disabled:opacity-50"
                onClick={() => onChange?.(defaultInvoiceDisplayOptions())}
              >
                Reset
              </button>
            ) : null}
          </div>
          {saving ? (
            <p
              className="invoice-preview-customize-panel__saving mt-0.5"
              aria-live="polite"
            >
              Saving…
            </p>
          ) : null}
        </div>
        <button
          type="button"
          disabled={disabled}
          className="mt-1.5 w-full rounded-md border border-slate-200 bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-700 hover:bg-slate-200/90 disabled:opacity-50"
          onClick={() => onChange?.(applyProviderOnlyPreset())}
        >
          Quick preset: Provider only
        </button>
      </div>

      <div className="invoice-preview-customize-panel__scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-1">
        <SectionBlock title="Header">
          <CheckRow
            id="inv-opt-logo"
            label="Logo"
            checked={opts.header.showLogo}
            disabled={disabled}
            onChange={(v) => patchGroup("header", "showLogo", v)}
          />
        </SectionBlock>

        <SectionBlock title="Bill to" disabled={toOff}>
          <CheckRow
            id="inv-opt-to-section"
            label="Show section"
            checked={opts.to.showSection}
            disabled={disabled}
            onChange={(v) => patchGroup("to", "showSection", v)}
          />
          <NestedGrid>
            <CheckRow
              id="inv-opt-to-name"
              label="Name"
              checked={opts.to.showName}
              disabled={disabled || toOff}
              onChange={(v) => patchGroup("to", "showName", v)}
            />
            <CheckRow
              id="inv-opt-to-email"
              label="Email"
              checked={opts.to.showEmail}
              disabled={disabled || toOff}
              onChange={(v) => patchGroup("to", "showEmail", v)}
            />
            <CheckRow
              id="inv-opt-to-phone"
              label="Phone"
              checked={opts.to.showPhone}
              disabled={disabled || toOff}
              onChange={(v) => patchGroup("to", "showPhone", v)}
            />
            <CheckRow
              id="inv-opt-to-country"
              label="Country"
              checked={opts.to.showCountry}
              disabled={disabled || toOff}
              onChange={(v) => patchGroup("to", "showCountry", v)}
            />
            <CheckRow
              id="inv-opt-to-campus"
              label="Campus"
              checked={opts.to.showCampus}
              disabled={disabled || toOff}
              onChange={(v) => patchGroup("to", "showCampus", v)}
            />
            <CheckRow
              id="inv-opt-to-address"
              label="Address"
              checked={opts.to.showBillingAddress}
              disabled={disabled || toOff}
              onChange={(v) => patchGroup("to", "showBillingAddress", v)}
            />
          </NestedGrid>
        </SectionBlock>

        <SectionBlock title="From" disabled={fromOff}>
          <CheckRow
            id="inv-opt-from-section"
            label="Show section"
            checked={opts.from.showSection}
            disabled={disabled}
            onChange={(v) => patchGroup("from", "showSection", v)}
          />
          <NestedGrid>
            <CheckRow
              id="inv-opt-from-name"
              label="Name"
              checked={opts.from.showName}
              disabled={disabled || fromOff}
              onChange={(v) => patchGroup("from", "showName", v)}
            />
            <CheckRow
              id="inv-opt-from-phone"
              label="Phone"
              checked={opts.from.showPhone}
              disabled={disabled || fromOff}
              onChange={(v) => patchGroup("from", "showPhone", v)}
            />
            <CheckRow
              id="inv-opt-from-email"
              label="Email"
              checked={opts.from.showEmail}
              disabled={disabled || fromOff}
              onChange={(v) => patchGroup("from", "showEmail", v)}
            />
            <CheckRow
              id="inv-opt-from-website"
              label="Website"
              checked={opts.from.showWebsite}
              disabled={disabled || fromOff}
              onChange={(v) => patchGroup("from", "showWebsite", v)}
            />
            <CheckRow
              id="inv-opt-from-address"
              label="Address"
              checked={opts.from.showAddress}
              disabled={disabled || fromOff}
              onChange={(v) => patchGroup("from", "showAddress", v)}
            />
          </NestedGrid>
        </SectionBlock>

        <SectionBlock title="Details" disabled={detailsOff}>
          <CheckRow
            id="inv-opt-details-section"
            label="Show section"
            checked={opts.details.showSection}
            disabled={disabled}
            onChange={(v) => patchGroup("details", "showSection", v)}
          />
          <NestedGrid>
            <CheckRow
              id="inv-opt-details-issue"
              label="Issue date"
              checked={opts.details.showIssueDate}
              disabled={disabled || detailsOff}
              onChange={(v) => patchGroup("details", "showIssueDate", v)}
            />
            <CheckRow
              id="inv-opt-details-number"
              label="Invoice #"
              checked={opts.details.showInvoiceNumber}
              disabled={disabled || detailsOff}
              onChange={(v) => patchGroup("details", "showInvoiceNumber", v)}
            />
            <CheckRow
              id="inv-opt-details-ref"
              label="Reference"
              checked={opts.details.showReference}
              disabled={disabled || detailsOff}
              onChange={(v) => patchGroup("details", "showReference", v)}
            />
            <CheckRow
              id="inv-opt-details-due"
              label="Due date"
              checked={opts.details.showDueDate}
              disabled={disabled || detailsOff}
              onChange={(v) => patchGroup("details", "showDueDate", v)}
            />
            <CheckRow
              id="inv-opt-details-payment"
              label="Paid date"
              checked={opts.details.showPaymentDate}
              disabled={disabled || detailsOff}
              onChange={(v) => patchGroup("details", "showPaymentDate", v)}
            />
            <CheckRow
              id="inv-opt-details-abn"
              label="ABN"
              checked={opts.details.showAbn}
              disabled={disabled || detailsOff}
              onChange={(v) => patchGroup("details", "showAbn", v)}
            />
          </NestedGrid>
        </SectionBlock>

        <SectionBlock title="Payment advice" disabled={adviceOff}>
          <CheckRow
            id="inv-opt-footer-advice"
            label="Show section"
            checked={opts.footer.showPaymentAdvice}
            disabled={disabled}
            onChange={(v) => patchGroup("footer", "showPaymentAdvice", v)}
          />
          <NestedGrid>
            <CheckRow
              id="inv-opt-advice-terms"
              label="Payment terms"
              checked={opts.footer.showPaymentTerms}
              disabled={disabled || adviceOff}
              onChange={(v) => patchGroup("footer", "showPaymentTerms", v)}
            />
            <CheckRow
              id="inv-opt-advice-account"
              label="Account name"
              checked={opts.footer.showAdviceAccountName}
              disabled={disabled || adviceOff}
              onChange={(v) => patchGroup("footer", "showAdviceAccountName", v)}
            />
            <CheckRow
              id="inv-opt-advice-bank"
              label="Bank"
              checked={opts.footer.showAdviceBank}
              disabled={disabled || adviceOff}
              onChange={(v) => patchGroup("footer", "showAdviceBank", v)}
            />
            <CheckRow
              id="inv-opt-advice-bsb"
              label="BSB"
              checked={opts.footer.showAdviceBsb}
              disabled={disabled || adviceOff}
              onChange={(v) => patchGroup("footer", "showAdviceBsb", v)}
            />
            <CheckRow
              id="inv-opt-advice-acct"
              label="Account number"
              checked={opts.footer.showAdviceAccountNumber}
              disabled={disabled || adviceOff}
              onChange={(v) =>
                patchGroup("footer", "showAdviceAccountNumber", v)
              }
            />
            <CheckRow
              id="inv-opt-advice-ref"
              label="Reference"
              checked={opts.footer.showAdviceReference}
              disabled={disabled || adviceOff}
              onChange={(v) => patchGroup("footer", "showAdviceReference", v)}
            />
            <CheckRow
              id="inv-opt-advice-contact"
              label="Payment queries"
              checked={opts.footer.showAdviceContact}
              disabled={disabled || adviceOff}
              onChange={(v) => patchGroup("footer", "showAdviceContact", v)}
            />
          </NestedGrid>
        </SectionBlock>

        <SectionBlock title="Terms">
          <CheckRow
            id="inv-opt-footer-terms"
            label="Terms & conditions"
            checked={opts.footer.showTerms}
            disabled={disabled}
            onChange={(v) => patchGroup("footer", "showTerms", v)}
          />
        </SectionBlock>
      </div>
    </div>
  );
}

export default InvoicePreviewCustomizePanel;
